import React from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import CustomAccordion from 'components/CustomAccordion';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { InstanceActivityLogEntry } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

type InstanceStatusLogAccordionProps = {
  instanceId: number;
};

const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const InstanceStatusLogAccordion: React.FC<InstanceStatusLogAccordionProps> = ({ instanceId }) => {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useQuery(
    ['ACTION_LIBRARY_INSTANCE_STATUS_LOGS', instanceId],
    () => actionLibraryInstancesApi.getInstanceStatusLogs(instanceId, { limit: 500 }),
    { enabled: instanceId > 0 },
  );

  const entries: InstanceActivityLogEntry[] = data?.data?.results ?? [];

  const content = () => {
    if (isError) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(action_library_messages.errors.something_went_wrong_try_later)}
        </Typography>
      );
    }

    if (isLoading && entries.length === 0) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(general_messages.loading)}
        </Typography>
      );
    }

    if (entries.length === 0) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(action_library_messages.instance.detail.action_log_empty)}
        </Typography>
      );
    }

    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>{t(action_library_messages.instance.detail.action_log_date)}</TableCell>
            <TableCell>{t(action_library_messages.instance.detail.action_log_user)}</TableCell>
            <TableCell>{t(action_library_messages.instance.detail.action_log_change)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(log => (
            <TableRow key={`${log.id}-${log.createdAt}`}>
              <TableCell>{formatDate(log.createdAt)}</TableCell>
              <TableCell>{log.userFullName || log.userEmail || '-'}</TableCell>
              <TableCell>{log.message ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <CenteredGrid
      component={CustomAccordion}
      heading={<Typography variant='h6'>{t(action_library_messages.instance.detail.action_log)}</Typography>}
      id='instance-status-log'
      width='lg'
    >
      {content()}
    </CenteredGrid>
  );
};

export default InstanceStatusLogAccordion;
